/**
 * Report designer geometry (ADR-0061 §4 — the WYSIWYG parity rule).
 *
 * The canvas and the server-side renderer BOTH derive geometry from the same
 * AST mm values; this module is the ONE mm↔px conversion the canvas uses
 * (the renderer emits literal CSS `mm` units, so parity holds by keeping a
 * single physical constant here — 1 mm = 96/25.4 CSS px, the CSS unit
 * definition itself). Pure: no React, no DOM.
 */

/** CSS reference pixels per mm at zoom 1 (the CSS `mm` unit definition). */
export const PX_PER_MM = 96 / 25.4;

/** Snap step for drag/resize, in mm. */
export const SNAP_MM = 1;

export function mmToPx(mm: number, zoom = 1): number {
  return mm * PX_PER_MM * zoom;
}

export function pxToMm(px: number, zoom = 1): number {
  return px / (PX_PER_MM * zoom);
}

/** Snap a mm value to the grid (never below 0). */
export function snapMm(mm: number, step = SNAP_MM): number {
  return Math.max(0, Math.round(mm / step) * step);
}

/** Clamp a control's box inside its lane: x within [0, laneW - w] and, when
 *  `laneHmm` is given, y within [0, laneH - h] — the band's height is an
 *  EXPLICIT property (resized via the band handle), so a drag can't stretch
 *  it elastically. Omitting `laneHmm` keeps y only bounded below (legacy).
 *  Width/height keep a minimum so a control can't collapse to invisibility. */
export function clampBox(
  box: { x: number; y: number; w: number; h: number },
  laneWmm: number,
  minSize = 1,
  laneHmm?: number,
): { x: number; y: number; w: number; h: number } {
  const w = Math.max(minSize, Math.min(box.w, laneWmm));
  const h = Math.max(minSize, laneHmm !== undefined ? Math.min(box.h, laneHmm) : box.h);
  const x = Math.max(0, Math.min(box.x, laneWmm - w));
  const y = Math.max(0, laneHmm !== undefined ? Math.min(box.y, laneHmm - h) : box.y);
  return { x, y, w, h };
}

/** Move a box by a pixel delta (pointer drag), snapped + clamped. */
export function moveBox(
  start: { x: number; y: number; w: number; h: number },
  dxPx: number,
  dyPx: number,
  zoom: number,
  laneWmm: number,
  laneHmm?: number,
): { x: number; y: number; w: number; h: number } {
  return clampBox(
    {
      ...start,
      x: snapMm(start.x + pxToMm(dxPx, zoom)),
      y: snapMm(start.y + pxToMm(dyPx, zoom)),
    },
    laneWmm,
    1,
    laneHmm,
  );
}

/** Resize a box from its SE corner by a pixel delta, snapped + clamped. */
export function resizeBox(
  start: { x: number; y: number; w: number; h: number },
  dxPx: number,
  dyPx: number,
  zoom: number,
  laneWmm: number,
  laneHmm?: number,
): { x: number; y: number; w: number; h: number } {
  return clampBox(
    {
      ...start,
      w: snapMm(Math.max(1, start.w + pxToMm(dxPx, zoom))),
      h: snapMm(Math.max(1, start.h + pxToMm(dyPx, zoom))),
    },
    laneWmm,
    1,
    laneHmm,
  );
}

/** Smart-guide snap (S4b): pull a moving box's edges/centers onto its
 *  SIBLINGS' edges/centers when within `threshold` mm — the alignment-guide
 *  behaviour of every pro designer. Returns the snapped box + the guide
 *  coordinates to draw (vertical = x values, horizontal = y values).
 *
 *  `xOnly` boxes contribute VERTICAL guides only: controls in OTHER bands
 *  share the lane's x origin (so x-alignment across bands is real) but each
 *  band has its OWN y origin — a cross-band y guide would lie. */
export function snapToSiblings(
  box: { x: number; y: number; w: number; h: number },
  siblings: { x: number; y: number; w: number; h: number }[],
  threshold = 0.8,
  xOnly: { x: number; y: number; w: number; h: number }[] = [],
): { box: { x: number; y: number; w: number; h: number }; vGuides: number[]; hGuides: number[] } {
  let best: { dx: number; guide: number } | null = null;
  let bestY: { dy: number; guide: number } | null = null;
  const xEdges = [box.x, box.x + box.w / 2, box.x + box.w];
  const yEdges = [box.y, box.y + box.h / 2, box.y + box.h];
  for (const s of [...siblings, ...xOnly]) {
    for (const sx of [s.x, s.x + s.w / 2, s.x + s.w]) {
      for (const bx of xEdges) {
        const d = sx - bx;
        if (Math.abs(d) <= threshold && (!best || Math.abs(d) < Math.abs(best.dx)))
          best = { dx: d, guide: sx };
      }
    }
  }
  for (const s of siblings) {
    for (const sy of [s.y, s.y + s.h / 2, s.y + s.h]) {
      for (const by of yEdges) {
        const d = sy - by;
        if (Math.abs(d) <= threshold && (!bestY || Math.abs(d) < Math.abs(bestY.dy)))
          bestY = { dy: d, guide: sy };
      }
    }
  }
  return {
    box: {
      ...box,
      x: Math.max(0, box.x + (best?.dx ?? 0)),
      y: Math.max(0, box.y + (bestY?.dy ?? 0)),
    },
    vGuides: best ? [best.guide] : [],
    hGuides: bestY ? [bestY.guide] : [],
  };
}

/** Ruler tick positions for a lane of `lengthMm`: minor every 5mm, labeled
 *  major every 10mm (the top mm ruler). */
export function rulerTicks(lengthMm: number): { mm: number; major: boolean }[] {
  const out: { mm: number; major: boolean }[] = [];
  for (let mm = 0; mm <= lengthMm; mm += 5) out.push({ mm, major: mm % 10 === 0 });
  return out;
}
